import { useEffect, useMemo } from "react";

/*
 * The stylesheet stays a static import so `[data-aos]` elements have their
 * initial (hidden) state from the very first paint, exactly as before.
 * Only the AOS *runtime* is loaded lazily: it never runs before the idle
 * callback below, so keeping it in the shared _app chunk only delayed
 * first paint for no benefit.
 */
import "aos/dist/aos.css";

let aosInitialized = false;
let aosModulePromise = null;

const loadAos = () => {
  if (!aosModulePromise) {
    aosModulePromise = import("aos").then((mod) => mod.default || mod);
  }

  return aosModulePromise;
};

function runAfterIdle({ minDelayMs = 1500, idleTimeoutMs = 3000 }, cb) {
  let cancelled = false;
  let t = null;
  let idleId = null;

  const safeRun = () => {
    if (!cancelled) cb();
  };

  t = setTimeout(() => {
    if (typeof window === "undefined") return safeRun();

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(safeRun, { timeout: idleTimeoutMs });
    } else {
      safeRun();
    }
  }, minDelayMs);

  return () => {
    cancelled = true;
    if (t) clearTimeout(t);
    if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
  };
}

export function useAOS(
  options,
  refreshOnRouteChangeKey,
  delayConfig = { minDelayMs: 1500, idleTimeoutMs: 3000, refreshDelayMs: 250 }
) {
  // تثبيت options (مرة واحدة)
  const stableOptions = useMemo(
    () => ({
      easing: "ease-out-cubic",
      once: true,
      offset: 50,
      ...(options || {}),
    }),
    []
  );

  // 1) Init متأخر (Idle + Delay)
  useEffect(() => {
    let cancelled = false;

    const cleanupIdle = runAfterIdle(
      { minDelayMs: delayConfig.minDelayMs, idleTimeoutMs: delayConfig.idleTimeoutMs },
      () => {
        loadAos().then((AOS) => {
          if (cancelled) return;

          if (!aosInitialized) {
            AOS.init(stableOptions);
            aosInitialized = true;
          } else {
            AOS.refresh();
          }
        });
      }
    );

    return () => {
      cancelled = true;
      cleanupIdle();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) Refresh بعد التنقل (لكن استنى شوية عشان DOM يثبت)
  useEffect(() => {
    if (!aosInitialized) return;
    if (!refreshOnRouteChangeKey) return;

    const t = setTimeout(() => {
      // لو تغيّر DOM كبير بعد route change
      loadAos().then((AOS) => AOS.refreshHard());
    }, delayConfig.refreshDelayMs ?? 250);

    return () => clearTimeout(t);
  }, [refreshOnRouteChangeKey, delayConfig.refreshDelayMs]);
}
